import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { newsAPI } from '../services/api';
import toast from 'react-hot-toast';

const NewsContext = createContext({});

export const useNews = () => useContext(NewsContext);

export const NewsProvider = ({ children }) => {
  const [news, setNews] = useState([]);
  const [featuredNews, setFeaturedNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    drafts: 0,
    views: 0
  });


  const fetchNews = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const result = await newsAPI.getNews(params);
      if (result.success) {
      
        setNews(result?.data.news || []);
        calculateStats(result?.data.news || []);
      }
      return result;
    } catch (error) {
     
      toast.error('Failed to load news');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFeaturedNews = useCallback(async (limit = 3) => {
    try {
      const result = await newsAPI.getFeaturedNews(limit);
      if (result.success) {
        setFeaturedNews(result.data || []);
      }
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const result = await newsAPI.getCategories();
      if (result.success) {
        setCategories(result.data || []);
      }
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const calculateStats = (newsData) => {
    const total = newsData.length;
    const published = newsData?.filter(n => n?.status === 'published')?.length;
    const drafts = newsData?.filter(n => n?.status === 'draft')?.length;
    const views = newsData?.reduce((sum, n) => sum + (n?.views || 0), 0);

    setStats({ total, published, drafts, views });
  };

  const getNews = async (id) => {
    try {
      const result = await newsAPI.getNewsItem(id);
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const createNews = async (newsData) => {
    try {
      const result = await newsAPI.createNews(newsData);
      if (result.success) {
        toast.success('News created successfully');
        await fetchNews();
        await fetchFeaturedNews();
      }
      return result;
    } catch (error) {
      toast.error('Failed to create news');
      return { success: false, error: error.message };
    }
  };

  const updateNews = async (id, newsData) => {
    try {
      const result = await newsAPI.updateNews(id, newsData);
      if (result.success) {
        toast.success('News updated successfully');
        await fetchNews();
        await fetchFeaturedNews();
      }
      return result;
    } catch (error) {
      toast.error('Failed to update news');
      return { success: false, error: error.message };
    }
  };

  const deleteNews = async (id) => {
    try {
      const result = await newsAPI.deleteNews(id);
      if (result.success) {
        toast.success('News deleted successfully');
        setNews(prev => prev.filter(n => n.id !== id));
        setFeaturedNews(prev => prev.filter(n => n.id !== id));
      }
      return result;
    } catch (error) {
      toast.error('Failed to delete news');
      return { success: false, error: error.message };
    }
  };

  const togglePublish = async (id) => {
    try {
      const result = await newsAPI.togglePublish(id);
      if (result.success) {
        toast.success('News status updated');
        await fetchNews();
        await fetchFeaturedNews();
      }
      return result;
    } catch (error) {
      toast.error('Failed to update news status');
      return { success: false, error: error.message };
    }
  };

  const incrementView = async (id) => {
    try {
      await newsAPI.incrementView(id);
    } catch (error) {
    }
  };

  useEffect(() => {
    fetchNews();
    fetchCategories();
    fetchFeaturedNews();
  }, [fetchNews, fetchCategories, fetchFeaturedNews]);

  const value = {
    news,
    featuredNews,
    categories,
    stats,
    loading,
    fetchNews,
    fetchFeaturedNews,
    fetchCategories,
    getNews,
    createNews,
    updateNews,
    deleteNews,
    togglePublish,
    incrementView
  };

  return (
    <NewsContext.Provider value={value}>
      {children}
    </NewsContext.Provider>
  );
};